import { addDays, todayIso, type BudgetPeriod, type Dashboard, type Transaction } from '@fin/shared';
import { useState } from 'react';
import { TransactionModal, type TxDefaults } from '../transactions/public.ts';
import { TxDetail } from '../transactions/public.ts';
import { Button, ConfirmButton, Empty, ErrorBox, Icon, Input, Menu, Modal, NumberInput, Select, useAction } from '../../components/ui.tsx';
import { del, patch, post } from '../../lib/api.ts';

import { rangeLabel } from '../../lib/format.ts';


import { invalidate, useQuery } from '../../lib/query.ts';
import { setQuery, useLocation } from '../../lib/router.tsx';
import { useWide } from './layout.tsx';
import { FreeHero } from './hero.tsx';
import { PlanList, Spending } from './lists.tsx';
import { PeriodOps, UpcomingEvents, PeriodEvents } from './operations.tsx';
import { TemplatesModal } from './editor.tsx';

export function PeriodPage() {
  const { query } = useLocation();
  const list = useQuery<BudgetPeriod[]>('/periods?limit=48');
  const dash = useQuery<Dashboard>('/dashboard');
  const today = todayIso();
  const periods = list.data ?? [];
  const selectedId = Number(query.get('id')) || periods.find((p) => p.startDate <= today && p.endDate >= today)?.id || periods[0]?.id;
  const idx = periods.findIndex((p) => p.id === selectedId);
  const period = periods[idx];
  const [modal, setModal] = useState<'budget' | 'templates' | null>(null);
  const [adding, setAdding] = useState<TxDefaults | null>(null);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const { busy, run } = useAction();
  const wide = useWide();

  if (list.error && !list.data) return <ErrorBox error={list.error} onRetry={list.reload} />;
  if (!list.data) return <PeriodSkeleton />;

  const createPeriod = (date: string) => run(async () => {
    const p = await post<BudgetPeriod>('/periods/generate', { date });
    await list.reload();
    invalidate('/dashboard');
    setQuery({ id: p.id });
  }, 'Период создан из шаблона');

  if (!period) {
    return (
      <div className="page">
        <Empty action={<Button variant="primary" busy={busy} onClick={() => createPeriod(today)}>Создать текущий период</Button>}>
          Периода пока нет. Он длится от одной зарплаты до следующей и заполняется из шаблона.
        </Empty>
      </div>
    );
  }

  const newer = periods[idx - 1];
  const older = periods[idx + 1];
  const refresh = () => invalidate('/periods', '/dashboard');

  return (
    <div className="page period">
      <div className="period-bar">
        <button type="button" className="icon-btn period-step" disabled={!older} onClick={() => older && setQuery({ id: older.id })} aria-label="Предыдущий период"><Icon name="left" /></button>
        <Select className="period-select" aria-label="Период" value={period.id} onChange={(e) => setQuery({ id: e.target.value })}>
          {periods.map((p) => <option key={p.id} value={p.id}>{rangeLabel(p.startDate, p.endDate)}</option>)}
        </Select>
        {newer ? (
          <button type="button" className="icon-btn period-step" onClick={() => setQuery({ id: newer.id })} aria-label="Следующий период"><Icon name="right" /></button>
        ) : (
          <button type="button" className="icon-btn period-step" disabled={busy} onClick={() => createPeriod(addDays(period.endDate, 1))} aria-label="Создать следующий период"><Icon name="plus" /></button>
        )}
        <Menu label="Действия с периодом" items={[
          { label: 'Изменить бюджет', icon: 'edit', onClick: () => setModal('budget') },
          { label: 'Шаблоны периодов', icon: 'calendar', onClick: () => setModal('templates') },
          { label: 'Сохранить как шаблон', icon: 'pin', onClick: () => void run(async () => { await post(`/periods/${period.id}/save-as-template`); }, 'Шаблон обновлён') },
        ]} />
      </div>

      {/* two columns from 1020px: the hero with spending on the left, operations and the bills on the right;
          one column keeps the old order (operations right after the hero) */}
      <div className="period-grid">
        <div className="col">
          {period.startDate <= today && period.endDate >= today ? <UpcomingEvents period={period} /> : null}
          <FreeHero period={period} dash={dash.data} onEditBudget={() => setModal('budget')} />
          {wide ? <Spending period={period} onPay={setAdding} onChanged={refresh} /> : <PeriodOps period={period} onOpen={setEditing} />}
        </div>
        <div className="col">
          {wide ? <PeriodOps period={period} onOpen={setEditing} /> : null}
          <PlanList period={period} section="mandatory" onPay={setAdding} onChanged={refresh} />
          {wide ? null : <Spending period={period} onPay={setAdding} onChanged={refresh} />}
          <PeriodEvents period={period} />
          <div className="danger-zone">
            <ConfirmButton confirmText="Удалить период со всеми строками?" onConfirm={async () => {
              await del(`/periods/${period.id}`);
              setQuery({ id: null });
              refresh();
            }}>Удалить период</ConfirmButton>
          </div>
        </div>
      </div>

      {modal === 'budget' ? <BudgetModal period={period} onClose={() => setModal(null)} onSaved={refresh} /> : null}
      {modal === 'templates' ? <TemplatesModal onClose={() => setModal(null)} /> : null}
      {adding ? <TransactionModal defaults={adding} onClose={() => setAdding(null)} /> : null}
      {editing ? <TxDetail tx={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

export function PeriodSkeleton() {
  return (
    <div className="page period" aria-busy="true" aria-label="Загружаю период">
      <div className="skeleton sk-bar" />
      <div className="period-grid">
        <div className="col"><div className="skeleton sk-hero" /><div className="skeleton sk-card" /></div>
        <div className="col"><div className="skeleton sk-card" /><div className="skeleton sk-card" /></div>
      </div>
    </div>
  );
}

export function BudgetModal({ period, onClose, onSaved }: { period: BudgetPeriod; onClose: () => void; onSaved: () => void }) {
  const [budget, setBudget] = useState<number | null>(period.budget);
  const [note, setNote] = useState(period.note);
  const { busy, run } = useAction();
  const save = () => run(async () => { await patch(`/periods/${period.id}`, { budget: budget ?? 0, note }); onSaved(); onClose(); }, 'Бюджет сохранён');
  return (
    <Modal className="modal-form" title={`Бюджет ${rangeLabel(period.startDate, period.endDate)}`} onClose={onClose} footer={<>
      <span /><div className="row-gap"><Button onClick={onClose}>Отмена</Button><Button variant="primary" busy={busy} onClick={save}>Сохранить</Button></div>
    </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <label className="field"><span className="field-label">Доход за период, ₽</span><NumberInput value={budget} onChange={setBudget} autoFocus /></label>
        <label className="field"><span className="field-label">Заметка</span><Input value={note} onChange={(e) => setNote(e.target.value)} /></label>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
